import { useCallback, useEffect, useMemo, useState } from "react";
import { useDebounce } from "../useDebounce";
import { createCache } from "../../module/cache";
import { Guide } from "../../types/type";
import { searchGuideFirestore } from "../../module/search/search.util";
import { stringUtils } from "../../module/search/generate";

// 검색 결과 타입 정의
export type GuideSearchResult = {
  guideList: Guide[];
};

export type Gender = "male" | "female" | "all";

export const useGuideSearch = (
  searchQuery: string,
  setIsTyping: (value: boolean) => void,
  gender: Gender
) => {
  const [searchResult, setSearchResult] = useState<Guide[]>([]);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [hasSearched, setHasSearched] = useState<boolean>(false);

  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  const searchCache = useMemo(
    () =>
      createCache<GuideSearchResult>({
        maxSize: 100,
        ttlMinutes: 30,
      }),
    []
  );

  const createCacheKey = useCallback(
    (query: string) => {
      return `${query.toLowerCase().trim()}_${gender}`;
    },
    [gender]
  );

  const performSearch = useCallback(
    async (query: string) => {
      if (query.trim() === "") {
        setSearchResult([]);
        setIsSearching(false);
        setHasSearched(false);
        return;
      }

      // 한글 한 글자 검색 방지
      if (stringUtils.isSingleKoreanCharacter(query)) {
        setSearchResult([]);
        setIsSearching(false);
        setHasSearched(true);
        return;
      }

      // 최소 2글자 이상 검색
      // if () {
      //   setSearchResult([]);
      //   setIsSearching(false);
      //   setHasSearched(true);
      //   return;
      // }

      setIsSearching(true);
      try {
        const cacheKey = createCacheKey(query);
        const cachedResult = searchCache.get(cacheKey);

        if (cachedResult) {
          setSearchResult(cachedResult.guideList);
          setIsTyping(false);
          setIsSearching(false);
          setHasSearched(true);
          return;
        }

        const result = await searchGuideFirestore(query, gender);
        setSearchResult(result.guideList);
        setIsTyping(false);
        setIsSearching(false);
        setHasSearched(true);
        searchCache.set(cacheKey, result);
      } catch (error) {
        console.error("Search error:", error);
        setSearchResult([]);
        setIsSearching(false);
        setHasSearched(true);
      }
    },
    [searchCache, createCacheKey, setIsTyping, gender]
  );

  useEffect(() => {
    if (searchQuery !== debouncedSearchQuery) {
      setIsTyping(true);
    }
  }, [searchQuery, debouncedSearchQuery]);

  useEffect(() => {
    performSearch(debouncedSearchQuery);
  }, [debouncedSearchQuery, performSearch]);

  useEffect(() => {
    const cleanup = setInterval(() => {
      searchCache.removeExpired();
    }, 5 * 60 * 1000);

    return () => {
      clearInterval(cleanup);
    };
  }, [searchCache]);

  const showFooter = hasSearched && !isSearching && searchQuery.length >= 2;

  return {
    searchResult,
    clearCache: useCallback(() => searchCache.clear(), [searchCache]),
    isEmptyResult: searchResult.length === 0,
    isSearching,
    showFooter,
  };
};
